import { Link } from 'react-router-dom';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Facebook,
  Instagram,
  Youtube,
  Leaf
} from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer
      id="contact"
      data-testid="footer"
      className="bg-[#1A1A1A] text-white"
    >
      <div className="section-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_54c61862-94c1-4446-b49b-c57faa7a3dc2/artifacts/pmn8bqyt_logo%20rm.jpg.jpeg"
                alt="AyurvedMed Healthcare"
                className="h-28 w-auto"
              />
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Authentic Ayurveda for Holistic Healing. Experience the ancient
              wisdom of natural medicine.
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#2D4A3E] transition-colors duration-300"
                data-testid="social-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#2D4A3E] transition-colors duration-300"
                data-testid="social-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#2D4A3E] transition-colors duration-300"
                data-testid="social-youtube"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-gray-400 hover:text-[#C67D63] transition-colors duration-200"
                  data-testid="footer-link-about"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('treatments')}
                  className="text-gray-400 hover:text-[#C67D63] transition-colors duration-200"
                  data-testid="footer-link-treatments"
                >
                  Treatments
                </button>
              </li>
              <li>
                <Link
                  to="/articles"
                  className="text-gray-400 hover:text-[#C67D63] transition-colors duration-200"
                  data-testid="footer-link-articles"
                >
                  Articles
                </Link>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('reviews')}
                  className="text-gray-400 hover:text-[#C67D63] transition-colors duration-200"
                  data-testid="footer-link-reviews"
                >
                  Reviews
                </button>
              </li>
            </ul>
          </div>

          {/* Treatments */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Our Treatments</h4>
            <ul className="space-y-3">
              <li>
                <span className="text-gray-400">Panchakarma Therapy</span>
              </li>
              <li>
                <span className="text-gray-400">Cancer Care</span>
              </li>
              <li>
                <span className="text-gray-400">Eye Care</span>
              </li>
              <li>
                <span className="text-gray-400">Kids & Women Health</span>
              </li>
              <li>
                <span className="text-gray-400">Joint & Spine Care</span>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C67D63] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400" data-testid="clinic-address">
                  77, Satya Ashraya Ayurvedmed,
                  <br />
                  5th Cross, Prithvi Layout, ECC Road,
                  <br />
                  Whitefield, Bangalore, Karnataka 560066
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#C67D63] flex-shrink-0" />
                <a
                  href="tel:+919986671810"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                  data-testid="clinic-phone"
                >
                  +91 99866 71810
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#C67D63] flex-shrink-0" />
                <a
                  href="mailto:ayurvedmedclinic@gmail.com"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                  data-testid="clinic-email"
                >
                  ayurvedmedclinic@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#C67D63] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400" data-testid="clinic-hours">
                  Monday - Sunday: 8:00 AM - 8:00 PM
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} AyurvedMed Healthcare. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <a
              href="#"
              className="text-gray-500 hover:text-white transition-colors duration-200"
            >
              Privacy Policy
            </a>
            <a
              href="#"
              className="text-gray-500 hover:text-white transition-colors duration-200"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
